import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { ArrowLeft, FileDown } from "lucide-react";

const OLIVE = "#3D4A3E", CREAM = "#F9F7F1", PAPER = "#FDFCF8", CLAY = "#B25D41",
  LEATHER = "#8C533E", INK = "#292A25", ROSE = "#C27D7A";
const SERIF = "'Cormorant Garamond', Georgia, serif";
const SANS = "'IBM Plex Sans', system-ui, sans-serif";

const Star = () => (
  <svg width="14" height="14" viewBox="0 0 24 24"><path d="M12 0 L14 10 L24 12 L14 14 L12 24 L10 14 L0 12 L10 10 Z" fill={CLAY} /></svg>
);
const Motif = ({ w = 190, rev = false }) => (
  <img src={rev ? "/brand/lr-ridge-motif-cover.png" : "/brand/lr-ridge-motif.png"} alt="" style={{ width: w, height: "auto" }} draggable="false" />
);

const P = ({ text }) => (text || "").split("\n").map((b) => b.trim()).filter(Boolean)
  .map((b, i) => <p key={i} style={{ margin: "0 0 12px" }}>{b}</p>);

const Eyebrow = ({ children, color = CLAY }) => (
  <div style={{ fontFamily: SANS, fontSize: 12, fontWeight: 600, letterSpacing: 2, textTransform: "uppercase", color }}>{children}</div>
);
const H1 = ({ children }) => (
  <h1 style={{ fontFamily: SERIF, fontWeight: 600, fontSize: 40, color: OLIVE, margin: "6px 0 22px", lineHeight: 1.05 }}>{children}</h1>
);

function Page({ eyebrow, title, biz, children, testid }) {
  return (
    <div data-testid={testid} style={{ background: PAPER, maxWidth: 860, margin: "0 auto 28px", padding: "40px 56px 30px", boxShadow: "0 1px 3px rgba(41,42,37,0.08)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", borderBottom: `1.5px solid`, borderImage: `linear-gradient(to right, ${OLIVE} 0 58%, ${CLAY} 58% 100%) 1`, paddingBottom: 8, marginBottom: 26 }}>
        <span style={{ fontFamily: SANS, fontSize: 11, fontWeight: 600, letterSpacing: 1.4, textTransform: "uppercase", color: OLIVE }}>{biz}</span>
        <span style={{ fontFamily: SANS, fontSize: 11, fontWeight: 600, letterSpacing: 1.4, textTransform: "uppercase", color: CLAY }}>{eyebrow}</span>
      </div>
      <Eyebrow>{eyebrow}</Eyebrow>
      <H1>{title}</H1>
      <div style={{ fontFamily: SANS, fontSize: 15, color: INK, lineHeight: 1.6 }}>{children}</div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: `1px solid rgba(178,93,65,0.4)`, marginTop: 34, paddingTop: 12 }}>
        <Motif w={150} />
        <img src="/brand/lr-logo-horizontal.png" alt="Long Range" style={{ width: 130, height: "auto" }} draggable="false" />
      </div>
    </div>
  );
}

export default function BlueprintPreview() {
  const { bid } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.get(`/businesses/${bid}/blueprint/document`).then((r) => setDoc(r.data)).catch(() => toast.error("Could not load Blueprint"));
  }, [bid]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/businesses/${bid}/blueprint/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = "Blueprint.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Blueprint PDF generated");
    } catch { toast.error("Could not generate PDF"); }
    finally { setDownloading(false); }
  };

  if (!doc) return <div className="p-8 text-lr-leather">Loading…</div>;

  const c = doc.cover, wf = doc.what_we_found, ws = doc.where_stands;
  const ff = doc.fix_first, plan = doc.plan.groups, opp = doc.opportunities, la = doc.leave_alone;
  const cc = doc.core_constraint;
  const cr = wf.client_reported || {};
  const biz = (c.business_name || "").toUpperCase();
  const svLines = (doc.short_version || "").split("\n").map((s) => s.trim()).filter(Boolean);
  const labels = ["The core issue", "Why it matters", "The direction", "What we leave alone", "In short"];
  const glance = [["Now", CLAY, "NOW"], ["Next", OLIVE, "NEXT"], ["Later", LEATHER, "LATER"]]
    .map(([lab, col, k]) => ({ lab, col, item: (plan[k] || [])[0] })).filter((x) => x.item);

  return (
    <div style={{ minHeight: "100vh", background: "#EDE7DA" }}>
      <div style={{ position: "sticky", top: 0, zIndex: 10 }} className="bg-lr-cream/95 backdrop-blur border-b hairline px-5 md:px-8 py-3 flex items-center justify-between gap-3">
        <button onClick={() => navigate(`/app/businesses/${bid}`)} className="text-sm text-lr-leather hover:text-lr-olive flex items-center gap-1" data-testid="preview-back">
          <ArrowLeft className="h-4 w-4" /> Back to workspace
        </button>
        <div className="flex items-center gap-3">
          <span className={`text-xs px-2.5 py-1 rounded-[3px] ${doc.status === "Ready" ? "bg-lr-olive/15 text-lr-olive" : "bg-lr-clay/15 text-lr-clay"}`}>{doc.status}</span>
          <Button onClick={downloadPdf} disabled={downloading} className="bg-lr-clay hover:bg-[#8d4f36] text-lr-cream rounded-[4px]" data-testid="preview-download-pdf">
            <FileDown className="h-4 w-4 mr-1.5" /> {downloading ? "Generating…" : "Download PDF"}
          </Button>
        </div>
      </div>

      <div style={{ padding: "28px 16px 60px" }}>
        {/* Cover */}
        <div data-testid="preview-cover" style={{ maxWidth: 860, margin: "0 auto 28px", background: OLIVE, color: CREAM, textAlign: "center", padding: "60px 40px 44px", boxShadow: "0 1px 3px rgba(41,42,37,0.15)" }}>
          <img src="/brand/lr-logo-horizontal-rev.png" alt="Long Range" style={{ width: "78%", maxWidth: 520, height: "auto", display: "block", margin: "0 auto" }} draggable="false" />
          <div style={{ margin: "48px 0 10px", display: "flex", justifyContent: "center" }}><Star /></div>
          <h1 style={{ fontFamily: SERIF, fontWeight: 600, fontSize: 46, letterSpacing: 1, color: CREAM, margin: "0 0 40px" }}>Business Blueprint</h1>
          <div style={{ fontFamily: SANS, fontSize: 11, letterSpacing: 3, textTransform: "uppercase", color: "#AEB6A6" }}>Prepared exclusively for</div>
          <div style={{ fontFamily: SERIF, fontSize: 28, color: CREAM, margin: "8px 0 4px" }}>{c.business_name}</div>
          <div style={{ fontFamily: SANS, fontSize: 11, letterSpacing: 1, color: "#AEB6A6" }}>{c.date}</div>
          <div style={{ marginTop: 46, display: "flex", justifyContent: "center" }}><Motif w={150} rev /></div>
        </div>

        {/* 1 Short Version */}
        {doc.short_version && (
          <Page eyebrow="The Short Version" title="The short version" biz={biz} testid="preview-section-short">
            <div style={{ display: "flex", gap: 40, flexWrap: "wrap" }}>
              <div style={{ flex: "1 1 240px" }}>
                <div style={{ width: 46, height: 3, background: CLAY, marginBottom: 20 }} />
                <p style={{ fontFamily: SERIF, fontStyle: "italic", fontWeight: 500, fontSize: 24, lineHeight: 1.3, color: INK, margin: 0 }}>{svLines[0]}</p>
              </div>
              <div style={{ flex: "1 1 300px" }}>
                {svLines.slice(1, 6).map((ln, i) => (
                  <div key={i} style={{ marginBottom: 20 }}>
                    <div style={{ fontFamily: SANS, fontSize: 11, fontWeight: 600, letterSpacing: 0.5, color: OLIVE, textTransform: "uppercase", marginBottom: 3 }}>{labels[i] || "Also"}</div>
                    <div style={{ fontSize: 15 }}>{ln}</div>
                  </div>
                ))}
              </div>
            </div>
            {glance.length > 0 && (
              <div style={{ background: CREAM, padding: "26px 30px", marginTop: 30 }}>
                <Eyebrow color={LEATHER}>At a glance</Eyebrow>
                <div style={{ display: "flex", gap: 24, marginTop: 14, flexWrap: "wrap" }}>
                  {glance.map((g) => (
                    <div key={g.lab} style={{ flex: "1 1 160px", fontSize: 14 }}>
                      <span style={{ display: "inline-block", width: 8, height: 8, background: g.col, marginRight: 6 }} /><b>{g.lab}</b>
                      <div style={{ color: LEATHER, fontSize: 13.5, lineHeight: 1.4 }}>{g.item.description}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Page>
        )}

        {/* 2 Where you stand */}
        {(ws.narrative || ws.objective || (ws.outcomes || []).length > 0) && (
          <Page eyebrow="Where You Stand" title="Where the business stands" biz={biz} testid="preview-section-where">
            <P text={ws.narrative} />
            {ws.objective && <><h3 style={{ fontFamily: SERIF, fontWeight: 600, fontSize: 18, margin: "18px 0 6px" }}>What the owner wants</h3><p>{ws.objective}</p></>}
            {(ws.outcomes || []).length > 0 && <><h3 style={{ fontFamily: SERIF, fontWeight: 600, fontSize: 18, margin: "18px 0 6px" }}>Outcomes that matter most</h3><ul style={{ paddingLeft: 18 }}>{ws.outcomes.map((o, i) => <li key={i} style={{ marginBottom: 4 }}>{o}</li>)}</ul></>}
          </Page>
        )}

        {/* 3 Findings */}
        {(wf.narrative || cr.biggest_problem || (cr.constraints || []).length || wf.constraints.length || wf.assets.length || wf.contradictions.length) && (
          <Page eyebrow="Findings" title="What we found" biz={biz} testid="preview-section-found">
            <P text={wf.narrative} />
            <div style={{ display: "flex", gap: 32, marginTop: 8, flexWrap: "wrap" }}>
              <div style={{ flex: "1 1 260px" }}>
                <Eyebrow color={OLIVE}>What you told us</Eyebrow>
                <div style={{ background: CREAM, padding: 26, marginTop: 12 }}>
                  {cr.biggest_problem && <p style={{ fontFamily: SERIF, fontStyle: "italic", fontSize: 16, color: OLIVE, margin: "0 0 16px", lineHeight: 1.35 }}>"{cr.biggest_problem}"</p>}
                  {(cr.constraints || []).length > 0 && <ul style={{ paddingLeft: 16, color: OLIVE, margin: 0 }}>{cr.constraints.map((x, i) => <li key={i} style={{ marginBottom: 8 }}>{x}</li>)}</ul>}
                </div>
              </div>
              <div style={{ flex: "1 1 260px" }}>
                <Eyebrow color={CLAY}>What Long Range found</Eyebrow>
                <div style={{ background: PAPER, borderTop: `2.5px solid ${CLAY}`, boxShadow: "0 0 0 1px rgba(140,83,62,0.22)", padding: 26, marginTop: 12 }}>
                  {[...wf.constraints.map((x) => [x.name, x.downstream_effects || x.business_impact]),
                    ...wf.assets.map((x) => [x.description, x.current_use]),
                    ...wf.contradictions.map((x) => [x.name, x.resolution])].map(([n, d], i) => (
                    <div key={i} style={{ marginBottom: 16, fontSize: 14.5, lineHeight: 1.45 }}><b style={{ color: INK }}>{n}</b>{d ? <><br /><span style={{ color: LEATHER }}>{d}</span></> : null}</div>
                  ))}
                </div>
              </div>
            </div>
          </Page>
        )}

        {/* 3b Core Constraint (convergence) */}
        {cc && (
          <Page eyebrow="The Core Constraint" title="What is actually driving it" biz={biz} testid="preview-section-core">
            {(cc.symptoms || []).length > 0 && (
              <div style={{ textAlign: "center", marginBottom: 8 }}>
                <div style={{ fontFamily: SANS, fontSize: 12, fontWeight: 600, letterSpacing: 1.5, textTransform: "uppercase", color: LEATHER, marginBottom: 18 }}>Several findings that look separate</div>
                <div style={{ lineHeight: 2 }}>
                  {cc.symptoms.map((s, i) => (
                    <span key={i} style={{ display: "inline-block", background: CREAM, color: INK, fontSize: 14, padding: "6px 18px", margin: "0 7px 9px", borderBottom: `2px solid rgba(178,93,65,0.45)` }}>{s}</span>
                  ))}
                </div>
                <div style={{ margin: "10px 0 4px", display: "flex", justifyContent: "center" }}><Star /></div>
                <div style={{ fontFamily: SERIF, fontStyle: "italic", fontSize: 17, color: LEATHER, marginBottom: 22 }}>converge on one underlying constraint</div>
              </div>
            )}
            <div style={{ background: OLIVE, color: CREAM, padding: "34px 38px" }}>
              <div style={{ fontFamily: SANS, fontSize: 11, fontWeight: 600, letterSpacing: 2, textTransform: "uppercase", color: "#AEB6A6" }}>The core constraint</div>
              <div style={{ fontFamily: SERIF, fontWeight: 600, fontSize: 34, lineHeight: 1.12, color: CREAM, marginTop: 6 }}>{cc.name}</div>
              {cc.explanation && <p style={{ fontFamily: SERIF, fontStyle: "italic", fontSize: 18, lineHeight: 1.4, color: "#E8E4D8", margin: "16px 0 0" }}>{cc.explanation}</p>}
              {(cc.upstream_causes || cc.downstream_effects) && (
                <div style={{ display: "flex", gap: 34, marginTop: 26, flexWrap: "wrap" }}>
                  {cc.upstream_causes && <div style={{ flex: "1 1 200px" }}><div style={{ fontFamily: SANS, fontSize: 10.5, fontWeight: 600, letterSpacing: 1.5, textTransform: "uppercase", color: "#AEB6A6", marginBottom: 6 }}>Why it persists</div><div style={{ fontSize: 14.5, lineHeight: 1.5, color: CREAM }}>{cc.upstream_causes}</div></div>}
                  {cc.downstream_effects && <div style={{ flex: "1 1 200px" }}><div style={{ fontFamily: SANS, fontSize: 10.5, fontWeight: 600, letterSpacing: 1.5, textTransform: "uppercase", color: "#AEB6A6", marginBottom: 6 }}>What it drives</div><div style={{ fontSize: 14.5, lineHeight: 1.5, color: CREAM }}>{cc.downstream_effects}</div></div>}
                </div>
              )}
              {cc.business_impact && <div style={{ marginTop: 26, paddingTop: 16, borderTop: `1px solid rgba(249,247,241,0.25)`, fontSize: 14.5, color: CREAM }}><span style={{ fontFamily: SANS, fontSize: 10.5, fontWeight: 600, letterSpacing: 1.5, textTransform: "uppercase", color: "#AEB6A6", marginRight: 6 }}>Impact</span>{cc.business_impact}</div>}
            </div>
          </Page>
        )}

        {/* 4 Fix first */}
        {(ff.narrative || (ff.items || []).length > 0) && (
          <Page eyebrow="Recommendations" title="What I would fix first" biz={biz} testid="preview-section-fix">
            <P text={ff.narrative} />
            {(ff.items || []).map((it, i) => i === 0 ? (
              <div key={it.id} style={{ borderTop: `3px solid ${CLAY}`, paddingTop: 18, marginTop: 24 }}>
                <div style={{ fontFamily: SANS, fontSize: 11, fontWeight: 600, letterSpacing: 1.5, textTransform: "uppercase", color: CLAY }}>Priority</div>
                <h3 style={{ fontFamily: SERIF, fontWeight: 600, fontSize: 22, color: OLIVE, margin: "6px 0 18px" }}>{it.description}</h3>
                <div style={{ display: "flex", gap: 24, marginBottom: 18, flexWrap: "wrap" }}>
                  {[["Expected benefit", it.expected_benefit], ["Cost", it.cost], ["Effort", it.difficulty || it.time_to_value]].filter(([, v]) => v).map(([l, v]) => (
                    <div key={l} style={{ flex: "1 1 140px" }}><div style={{ fontFamily: SANS, fontSize: 10.5, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", color: LEATHER, marginBottom: 3 }}>{l}</div><div style={{ fontSize: 15 }}>{v}</div></div>
                  ))}
                </div>
                {(it.why_it_matters || it.impact) && <p>{it.why_it_matters || it.impact}</p>}
                {it.reasoning && <p>{it.reasoning}</p>}
              </div>
            ) : (
              <div key={it.id} style={{ borderTop: "1px solid rgba(61,74,62,0.3)", paddingTop: 16, margin: "28px 0 0 40px" }}>
                <h3 style={{ fontFamily: SERIF, fontWeight: 600, fontSize: 17, color: INK, margin: "0 0 4px" }}>{it.description}</h3>
                {(it.difficulty || it.why_it_matters) && <p style={{ color: LEATHER, margin: 0 }}><span style={{ fontFamily: SANS, fontSize: 10.5, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase" }}>Effort</span> {it.difficulty || "See below"}. {it.why_it_matters || ""}</p>}
              </div>
            ))}
          </Page>
        )}

        {/* 5 The plan */}
        {["NOW", "NEXT", "LATER", "PREPARE NEXT"].some((k) => (plan[k] || []).length) && (
          <Page eyebrow="The Plan" title="The plan" biz={biz} testid="preview-section-plan">
            <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
              {[["Now", CLAY, "NOW"], ["Next", OLIVE, "NEXT"], ["Later", LEATHER, "LATER"], ["Prepare next", LEATHER, "PREPARE NEXT"]].map(([lab, col, k]) => (plan[k] || []).length > 0 && (
                <div key={k} style={{ flex: "1 1 200px" }}>
                  <div style={{ height: 3, background: col, marginBottom: 14 }} />
                  <div style={{ fontFamily: SANS, fontSize: 13, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", color: col, marginBottom: 12 }}>{lab}</div>
                  {plan[k].map((it) => <div key={it.id} style={{ fontSize: 14.5, lineHeight: 1.45, marginBottom: 12, paddingLeft: 14, textIndent: -14 }}><span style={{ display: "inline-block", width: 7, height: 7, background: col, marginRight: 6 }} />{it.description}</div>)}
                </div>
              ))}
            </div>
          </Page>
        )}

        {/* 6 Opportunities */}
        {(opp.narrative || (opp.assets || []).length > 0) && (
          <Page eyebrow="Opportunities" title="Opportunities worth considering" biz={biz} testid="preview-section-opp">
            <P text={opp.narrative} />
            {(opp.assets || []).map((a) => (
              <div key={a.id} style={{ fontSize: 15, lineHeight: 1.5, marginBottom: 14, paddingLeft: 16, position: "relative" }}>
                <span style={{ position: "absolute", left: 0, top: 8, width: 6, height: 6, background: OLIVE }} /><b>{a.description}</b>{a.opportunity ? ` ${a.opportunity}` : ""}
              </div>
            ))}
          </Page>
        )}

        {/* 7 Leave alone */}
        {(la.narrative || (la.items || []).length > 0) && (
          <Page eyebrow="Leave Alone" title="What I would leave alone" biz={biz} testid="preview-section-leave">
            <P text={la.narrative} />
            {(la.items || []).map((x) => (
              <div key={x.id} style={{ fontSize: 15, lineHeight: 1.5, marginBottom: 14, paddingLeft: 16, position: "relative" }}>
                <span style={{ position: "absolute", left: 0, top: 8, width: 6, height: 6, background: ROSE }} /><b>{x.name}</b>{x.reason ? ` ${x.reason}` : ""}
              </div>
            ))}
          </Page>
        )}

        {/* 8 90 day */}
        {doc.ninety_day && <Page eyebrow="90 Day Blueprint" title="The first 90 days" biz={biz} testid="preview-section-ninety"><P text={doc.ninety_day} /></Page>}
        {/* 9 Next steps */}
        {doc.next_steps && <Page eyebrow="Next Steps" title="Next steps" biz={biz} testid="preview-section-next"><P text={doc.next_steps} /></Page>}
      </div>
    </div>
  );
}
